<template>
  <div
    v-if="visible"
    data-testid="modal-backdrop"
    class="modal-backdrop"
    :class="{ 'modal-backdrop--no': noBackdrop }"
  >
    <section
      class="modal-window"
      :class="[
        `border-radius--${borderRadiusSize}`,
        {
          [size] : !maxWidth,
          'modal--small-padding' : smallPadding && !withoutPadding,
          'modal--without-padding': withoutPadding
        }
      ]"
      :style="`max-width: ${maxWidth}px;`"
      role="dialog"
      @click="$emit('overlayClick', $event)"
    >
      <h3
        v-if="showTitle"
        class="model-window__title"
        :class="{'with-close-button': showClose}"
      >
        {{ title }}
      </h3>
      <span
        v-if="showLabel && label"
        class="model-window__label"
      >
        {{ label }}
      </span>
      <header
        v-if="$slots.header"
      >
        <slot
          name="header"
        />
      </header>
      <slot
        name="close-button"
      >
        <ModalCloseBtn
          v-if="showClose"
          :close-circle="closeCircle"
          :filled-close-icon="filledCloseIcon"
          @click="onCloseClick"
        />
      </slot>
      <div
        class="modal-window__body"
      >
        <slot />
      </div>

      <footer
        v-if="$slots.footer"
      >
        <slot
          name="footer"
        />
      </footer>
    </section>
  </div>
</template>

<script>
import ModalCloseBtn from '@/components/button/ModalCloseBtn.vue'

export default {
  name: 'ModalWindow',
  components: { ModalCloseBtn },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    showClose: {
      type: Boolean,
      default: true
    },
    escClose: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['xxl', 'xl', 'lg', 'md', 'sm'].includes(value)
      }
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    smallPadding: {
      type: Boolean,
      default: false
    },
    withoutPadding: {
      type: Boolean,
      default: false
    },
    noScroll: {
      type: Boolean,
      default: false
    },
    maxWidth: {
      type: [Number, Object],
      default: null
    },
    borderRadiusSize: {
      type: String,
      default: 'sm',
      validator(value) {
        return ['sm', 'md', 'lg', 'xl'].includes(value)
      }
    },
    noBackdrop: {
      type: Boolean,
      default: false
    },
    closeCircle: {
      type: Boolean,
      default: false
    },
    filledCloseIcon: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'onClose', 'overlayClick'],
  data() {
    return {
      escClickListener: null
    }
  },
  watch: {
    visible: {
      handler(value) {
        if (value) {
          document.addEventListener('keyup', this.onEscPress)
          document.body.classList.add('noscroll')
        } else {
          document.removeEventListener('keyup', this.onEscPress)
          this.$nextTick(() => {
            const anotherWindowOpened =
              document.querySelectorAll('.modal-backdrop').length
            if (!anotherWindowOpened) {
              document.body.classList.remove('noscroll')
            }
          })
        }
      },
      immediate: true
    }
  },
  unmounted() {
    document.body.classList.remove('noscroll')
  },
  methods: {
    hide(closeType = null) {
      this.$emit('onClose', closeType)
      this.$emit('update:visible', false)
    },
    onCloseClick() {
      this.hide('onCloseClick')
    },
    onEscPress({ keyCode }) {
      const { escClose } = this
      if (!escClose) {
        return
      }
      if (keyCode === 27) {
        this.hide('onEscPress')
      }
    }
  }
}
</script>

<style
  scoped
  lang="scss"
>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh; // fallback
  height: 100dvh;
  z-index: 2000;
  background: var(--color-opacity-black-30);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: var(--space-48) var(--space-20);
  -webkit-overflow-scrolling: touch;
  transform: translateZ(0);

  &--no {
    background: transparent;
  }

  @include phone {
    padding: var(--space-48) 0;
  }

  .modal-window {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    background: var(--color-white);
    max-height: unset;
    gap: var(--space-12);
    padding: var(--space-32);

    &.sm {
      max-width: 372px;
    }

    &.md {
      max-width: 430px;
    }

    .modal-window__body {
      flex-grow: 1;
      min-height: 80px;
    }

    &.lg {
      max-width: 520px;
      width: 100%;

      .modal-window__body {
        height: auto;
      }
    }

    &.xl {
      max-width: 570px;
      width: 100%;
    }

    &.xxl {
      max-width: 750px;
      width: 100%;
    }

    &.modal--small-padding {
      padding: var(--space-12);
    }

    &.modal--without-padding {
      padding: 0;
    }

    &.border-radius--sm {
      border-radius: var(--border-radius-4);
    }

    &.border-radius--md {
      border-radius: var(--border-radius-12);
    }

    &.border-radius--lg {
      border-radius: var(--border-radius-24);
    }

    &.border-radius--xl {
      border-radius: var(--border-radius-32);
    }

    @include phone {
      border-radius: var(--border-radius-12) var(--border-radius-12) 0 0;
      width: 100vw;

      .modal-window__body {
        min-height: unset;
      }

      &.border-radius--lg {
        border-radius: var(--border-radius-4);
      }
    }

    .model-window__title {
      margin: 0;
      font-family: Nohemi, sans-serif;
      font-size: var(--subtitle-1);
      line-height: var(--leading-160);
      font-weight: var(--weight-semibold);
      color: var(--color-zinc-700);
      min-height: var(--space-48);
      display: flex;
      align-items: center;

      &.with-close-button {
        padding-right: var(--space-72);
      }
    }

    .model-window__label {
      font-family: Nohemi, sans-serif;
      font-size: var(--font-size-body-1);
      line-height: var(--leading-180);
      font-weight: var(--weight-normal);
      color: var(--color-zinc-400);
      display: flex;
      align-items: center;
      margin-top: var(--space-12);
    }
  }
}
</style>
